<script setup lang="ts">
/**
 * First-run welcome tour. Teaches the one idea that makes Escherllate make
 * sense — draw once, draw everywhere — then the modes, tools, and saving.
 * Shown automatically until the user has been onboarded once (ui store), and
 * replayable from the Help sheet.
 */
import { ref } from 'vue';
import {
  IconChevronLeft,
  IconChevronRight,
  IconX,
  IconPencil,
  IconVectorBezier,
  IconGhost2,
  IconFolders,
} from '@tabler/icons-vue';
import AppLogo from '@/components/AppLogo.vue';
import { useUiStore } from '@/stores/ui';

const ui = useUiStore();

const step = ref(0);

interface Slide {
  title: string;
  body: string;
  hint?: string;
}

const slides: Slide[] = [
  {
    title: 'Draw once, draw everywhere',
    body: 'Escherllate is a sketchbook for tessellations. The solid tile in the middle is your canvas; the fainter tiles around it are live copies. Draw on the middle tile and your line appears on every copy at once.',
    hint: 'Try a scribble — watch it echo across the pattern.',
  },
  {
    title: 'Lines wrap across the edges',
    body: "Run a stroke off the edge of the tile and it doesn't stop — it wraps around and continues seamlessly on the neighbour. That's what makes a perfect, repeating pattern feel like ordinary drawing.",
    hint: 'Deliberately draw off one side and watch it re-enter on the other.',
  },
  {
    title: 'Switch the symmetry',
    body: 'In the Pattern panel, choose a shape and a symmetry — Translation, Half turn, Quarter turn, Glide and more. The same drawing re-tiles instantly under each rule. Quarter turn gives the classic Escher pinwheel.',
    hint: 'The most fun button in the app — try them all on one doodle.',
  },
  {
    title: 'Draw, erase, trace & save',
    body: 'Draw with a textured coloured pencil; Trace (Pro) makes clean vector outlines. Use layers to refine. Everything autosaves to your device, privately — find it under Sketches.',
  },
];

function next(): void {
  if (step.value < slides.length - 1) step.value++;
  else ui.completeOnboarding();
}

function back(): void {
  if (step.value > 0) step.value--;
}

function skip(): void {
  ui.completeOnboarding();
}
</script>

<template>
  <div v-if="ui.showOnboarding" class="onb-backdrop">
    <div class="onb" role="dialog" aria-modal="true" aria-label="Welcome to Escherllate">
      <button class="onb-skip" @click="skip">
        Skip
        <IconX :size="14" stroke-width="2" />
      </button>

      <div class="onb-art" aria-hidden="true">
        <AppLogo v-if="step === 0" :size="56" />
        <IconGhost2 v-else-if="step === 1" :size="52" stroke-width="1.25" />
        <div v-else-if="step === 2" class="sym-grid">
          <span /><span /><span /><span />
        </div>
        <div v-else class="mode-row">
          <IconPencil :size="30" stroke-width="1.5" />
          <IconVectorBezier :size="30" stroke-width="1.5" />
          <IconFolders :size="30" stroke-width="1.5" />
        </div>
      </div>

      <h2>{{ slides[step].title }}</h2>
      <p class="onb-body">{{ slides[step].body }}</p>
      <p v-if="slides[step].hint" class="onb-hint">{{ slides[step].hint }}</p>

      <div class="onb-dots" aria-hidden="true">
        <span v-for="(_, i) in slides" :key="i" :class="{ on: i === step }" />
      </div>

      <div class="onb-nav">
        <button class="onb-btn ghost" :disabled="step === 0" @click="back">
          <IconChevronLeft :size="16" stroke-width="2" />
          Back
        </button>
        <button class="onb-btn primary" @click="next">
          {{ step === slides.length - 1 ? 'Start drawing' : 'Next' }}
          <IconChevronRight v-if="step < slides.length - 1" :size="16" stroke-width="2" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.onb-backdrop {
  position: fixed;
  inset: 0;
  z-index: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(16px, var(--safe-top)) 16px max(16px, var(--safe-bottom));
  background: rgba(8, 8, 16, 0.78);
}

.onb {
  position: relative;
  width: min(440px, 100%);
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  border-radius: 16px;
  padding: 28px 24px 22px;
  text-align: center;
}

.onb-skip {
  position: absolute;
  top: 12px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 12px;
  color: var(--text-muted);
  background: transparent;
  border: none;
  cursor: pointer;
}

.onb-skip:hover {
  color: var(--text-primary);
}

.onb-art {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 72px;
  margin-bottom: 14px;
  color: var(--accent);
}

.mode-row {
  display: flex;
  gap: 14px;
  color: var(--accent);
}

.sym-grid {
  display: grid;
  grid-template-columns: repeat(2, 26px);
  grid-template-rows: repeat(2, 26px);
  gap: 3px;
}

.sym-grid span {
  border: 1.5px solid var(--accent);
  border-radius: 3px;
}

.sym-grid span:nth-child(2) {
  transform: rotate(90deg);
}
.sym-grid span:nth-child(3) {
  transform: rotate(270deg);
}
.sym-grid span:nth-child(4) {
  transform: rotate(180deg);
}

.onb h2 {
  margin: 0 0 10px;
  font-size: 19px;
  color: var(--text-primary);
}

.onb-body {
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-muted);
}

.onb-hint {
  margin: 0 0 4px;
  font-size: 12.5px;
  font-style: italic;
  color: var(--accent);
}

.onb-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin: 16px 0;
}

.onb-dots span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ui-border);
  transition: background 0.15s;
}

.onb-dots span.on {
  background: var(--accent);
}

.onb-nav {
  display: flex;
  gap: 10px;
}

.onb-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: 44px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 9px;
  cursor: pointer;
}

.onb-btn.ghost {
  background: transparent;
  color: var(--text-primary);
  border: 1px solid var(--ui-border);
}

.onb-btn.ghost:disabled {
  opacity: 0.35;
  cursor: default;
}

.onb-btn.primary {
  background: var(--accent);
  color: var(--accent-contrast);
  border: none;
}
</style>
